// Split antimeridian-crossing triangles at lon=180 when loading tiles.
// Only crossing triangles get new vertices; the rest keep shared indices.

const MERIDIAN = 180.0;

function triangleLonSpan(lons) {
  return Math.max(lons[0], lons[1], lons[2]) - Math.min(lons[0], lons[1], lons[2]);
}

function unwrapLon(lon) {
  return lon < 0 ? lon + 360 : lon;
}

function intersectMeridian(prev, cur) {
  const t = (MERIDIAN - prev.ulon) / (cur.ulon - prev.ulon);
  const lat = prev.lat + t * (cur.lat - prev.lat);
  return {ulon: MERIDIAN, lat, src: [prev.src, cur.src, t]};
}

function makeVert(lon, lat, src) {
  return {lon, lat, src};
}

function sampleVertexAttribute(src, values, componentsPerVertex, c) {
  if (typeof src === 'number') {
    return values[src * componentsPerVertex + c];
  }
  const [a, b, t] = src;
  const va = sampleVertexAttribute(a, values, componentsPerVertex, c);
  const vb = sampleVertexAttribute(b, values, componentsPerVertex, c);
  return va + t * (vb - va);
}

function clipPolygonLonSide(poly, side) {
  if (!poly.length) return [];
  const out = [];
  const n = poly.length;
  for (let i = 0; i < n; i++) {
    const cur = poly[i];
    const prev = poly[(i - 1 + n) % n];
    const curIn = side === 'west' ? cur.ulon <= MERIDIAN : cur.ulon >= MERIDIAN;
    const prevIn = side === 'west' ? prev.ulon <= MERIDIAN : prev.ulon >= MERIDIAN;
    if (curIn) {
      if (!prevIn) out.push(intersectMeridian(prev, cur));
      out.push(cur);
    } else if (prevIn) {
      out.push(intersectMeridian(prev, cur));
    }
  }
  return out;
}

function triangulateFan(poly) {
  if (poly.length < 3) return [];
  if (poly.length === 3) return [poly];
  const tris = [];
  for (let i = 1; i < poly.length - 1; i++) {
    tris.push([poly[0], poly[i], poly[i + 1]]);
  }
  return tris;
}

function ulonToRenderLon(ulon, side) {
  return side === 'east' ? ulon - 360 : ulon;
}

function splitCrossingTriangle(i0, i1, i2, positions2, stats) {
  const lons = [positions2[i0 * 2], positions2[i1 * 2], positions2[i2 * 2]];
  const lats = [positions2[i0 * 2 + 1], positions2[i1 * 2 + 1], positions2[i2 * 2 + 1]];
  const srcs = [i0, i1, i2];
  const ulons = lons.map(unwrapLon);
  const poly = ulons.map((ulon, j) => ({ulon, lat: lats[j], src: srcs[j]}));

  const pieces = [];
  for (const side of ['west', 'east']) {
    const clipped = clipPolygonLonSide(poly, side);
    for (const tri of triangulateFan(clipped)) {
      const renderLons = tri.map(v => ulonToRenderLon(v.ulon, side));
      if (triangleLonSpan(renderLons) > 180) {
        stats.piecesDropped++;
        continue;
      }
      stats.piecesKept++;
      pieces.push(tri.map((v, j) => makeVert(renderLons[j], v.lat, v.src)));
    }
  }
  return pieces;
}

export function splitAntimeridianGeometry(positions2, indices) {
  const nVerts = positions2.length / 2;
  const nTris = indices.length / 3;
  let needsSplit = false;
  for (let t = 0; t < nTris; t++) {
    const i0 = indices[t * 3];
    const i1 = indices[t * 3 + 1];
    const i2 = indices[t * 3 + 2];
    const lons = [positions2[i0 * 2], positions2[i1 * 2], positions2[i2 * 2]];
    if (triangleLonSpan(lons) > 180) {
      needsSplit = true;
      break;
    }
  }
  if (!needsSplit) {
    return {positions2, indices, vertexSources: null, splitStats: null};
  }

  const stats = {
    srcTris: nTris,
    srcCrossing: 0,
    piecesKept: 0,
    piecesDropped: 0,
    samples: []
  };

  const lonBuf = Array.from(positions2);
  const vertexSources = Array.from({length: nVerts}, (_, i) => i);
  const outIdx = [];

  for (let t = 0; t < nTris; t++) {
    const i0 = indices[t * 3];
    const i1 = indices[t * 3 + 1];
    const i2 = indices[t * 3 + 2];
    const lons = [positions2[i0 * 2], positions2[i1 * 2], positions2[i2 * 2]];

    if (triangleLonSpan(lons) <= 180) {
      outIdx.push(i0, i1, i2);
      continue;
    }

    stats.srcCrossing++;
    const pieces = splitCrossingTriangle(i0, i1, i2, positions2, stats);
    for (const tri of pieces) {
      const base = vertexSources.length;
      for (const v of tri) {
        lonBuf.push(v.lon, v.lat);
        vertexSources.push(v.src);
      }
      outIdx.push(base, base + 1, base + 2);
    }
  }

  const outPos = new Float32Array(lonBuf);
  stats.outTris = outIdx.length / 3;
  stats.outVerts = vertexSources.length;
  let lonMin = Infinity;
  let lonMax = -Infinity;
  for (let i = 0; i < outPos.length; i += 2) {
    const lon = outPos[i];
    if (lon < lonMin) lonMin = lon;
    if (lon > lonMax) lonMax = lon;
  }
  stats.outLonRange = [lonMin, lonMax];

  return {
    positions2: outPos,
    indices: new Uint32Array(outIdx),
    vertexSources,
    splitStats: stats
  };
}

export function expandVertexAttribute(values, vertexSources, componentsPerVertex = 1) {
  if (!vertexSources) return values;
  const out = new Float32Array(vertexSources.length * componentsPerVertex);
  for (let i = 0; i < vertexSources.length; i++) {
    const src = vertexSources[i];
    for (let c = 0; c < componentsPerVertex; c++) {
      out[i * componentsPerVertex + c] = sampleVertexAttribute(src, values, componentsPerVertex, c);
    }
  }
  return out;
}

export function longitudeInBounds(lon, west, east) {
  for (const offset of [0, -360, 360]) {
    const l = lon + offset;
    if (l >= west && l <= east) return true;
  }
  return false;
}
